import type { EventoAdmin, Pregunta, ValorRespuesta } from '../../api/tipos';
import { lineaDeEvento } from '../../lib/fechas';

// Las librerías de Excel y PDF se cargan recién al exportar, para no sumarle
// peso al bundle de todos los que solo entran a responder.

/// Texto legible de lo que respondió una persona en una pregunta.
/// null cuando no la respondió (o no le correspondía, si es condicional).
export function textoDeValor(pregunta: Pregunta, valor: ValorRespuesta | undefined): string | null {
  if (!valor) return null;

  const textos = pregunta.opciones
    .filter((opcion) => valor.opcionIds.includes(opcion.id))
    .map((opcion) => opcion.texto);
  return textos.length > 0 ? textos.join(', ') : null;
}

const valorDe = (valores: ValorRespuesta[], pregunta: Pregunta) =>
  valores.find((valor) => valor.preguntaId === pregunta.id);

/// «Cumpleaños de septiembre» → «respuestas-cumpleanos-de-septiembre-2026-09-30»
function nombreDeArchivo(evento: EventoAdmin, extension: string): string {
  const base = evento.titulo
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `respuestas-${base || 'evento'}-${evento.fechaLocal}.${extension}`;
}

/// Planilla con todas las respuestas: quienes van y quienes no, y cada campo extra.
export async function exportarExcel(evento: EventoAdmin): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');

  const encabezado = { fontWeight: 'bold' as const, backgroundColor: '#E8EEF5' };

  const filas = [
    [
      { value: 'Colaborador', ...encabezado },
      { value: 'Correo', ...encabezado },
      { value: '¿Asiste?', ...encabezado },
      ...evento.preguntas.map((pregunta) => ({
        value: pregunta.privada ? `${pregunta.etiqueta} (privada)` : pregunta.etiqueta,
        ...encabezado,
      })),
    ],
    ...evento.respuestas.map((respuesta) => [
      { value: respuesta.usuario.nombre },
      { value: respuesta.usuario.email },
      { value: respuesta.asiste ? 'Sí' : 'No' },
      ...evento.preguntas.map((pregunta) => ({
        value: textoDeValor(pregunta, valorDe(respuesta.valores, pregunta)) ?? '',
      })),
    ]),
  ];

  await writeXlsxFile(filas, {
    sheet: 'Respuestas',
    stickyRowsCount: 1,
    columns: [{ width: 28 }, { width: 32 }, { width: 10 }, ...evento.preguntas.map(() => ({ width: 22 }))],
  }).toFile(nombreDeArchivo(evento, 'xlsx'));
}

/// Lista para imprimir y llevar al evento: solo quienes confirmaron, con un
/// cuadro al lado del nombre para tildar a los que van llegando.
export async function exportarPdf(evento: EventoAdmin): Promise<void> {
  const [{ jsPDF }, { autoTable }] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);

  const asistentes = evento.respuestas.filter((respuesta) => respuesta.asiste);
  // Con muchos campos extra la hoja vertical no alcanza.
  const orientacion = evento.preguntas.length > 3 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation: orientacion, unit: 'mm', format: 'a4' });
  const margen = 14;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(evento.titulo, margen, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90);
  doc.text(lineaDeEvento(evento.fechaLocal, evento.horaLocal, evento.lugar), margen, 25);
  doc.text(
    `${asistentes.length} ${asistentes.length === 1 ? 'confirmado' : 'confirmados'} · Llegaron: ______`,
    margen,
    31,
  );
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 36,
    margin: { left: margen, right: margen, bottom: 16 },
    head: [['', 'Colaborador', ...evento.preguntas.map((pregunta) => pregunta.etiqueta)]],
    body: asistentes.map((respuesta) => [
      '',
      respuesta.usuario.nombre,
      // Guion simple: las fuentes base de jsPDF no traen la raya (—).
      ...evento.preguntas.map(
        (pregunta) => textoDeValor(pregunta, valorDe(respuesta.valores, pregunta)) ?? '-',
      ),
    ]),
    theme: 'grid',
    styles: { font: 'helvetica', fontSize: 9.5, cellPadding: 2.4, valign: 'middle', lineColor: 200 },
    headStyles: { fillColor: [17, 102, 176], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 11 },
      1: { cellWidth: orientacion === 'portrait' ? 62 : 70, fontStyle: 'bold' },
    },
    didDrawCell: (celda) => {
      if (celda.section !== 'body' || celda.column.index !== 0) return;
      const lado = 4.5;
      doc.setDrawColor(60);
      doc.setLineWidth(0.3);
      doc.rect(
        celda.cell.x + (celda.cell.width - lado) / 2,
        celda.cell.y + (celda.cell.height - lado) / 2,
        lado,
        lado,
      );
    },
    didDrawPage: () => {
      const { width, height } = doc.internal.pageSize;
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(`Página ${doc.getNumberOfPages()}`, width - margen, height - 8, { align: 'right' });
      doc.setTextColor(0);
    },
  });

  if (asistentes.length === 0) {
    doc.setFontSize(10);
    doc.text('Todavía nadie confirmó asistencia.', margen, 50);
  }

  doc.save(nombreDeArchivo(evento, 'pdf'));
}
